import React, { useState } from 'react';
import { FaStar } from 'react-icons/fa';
import apiClient from '../api/api';

const RatingModal = ({ isOpen, onClose, workerName, workerId, hireId, type, onSuccess, targets = [] }) => {
    const [rating, setRating] = useState(5);
    const [review, setReview] = useState('');
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const isBulk = targets.length > 1;
    const finalTargets = isBulk ? targets : (workerId ? [{ workerId, workerName, hireId, type }] : []);

    if (finalTargets.length === 0 && !isBulk) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (finalTargets.length === 0) {
            alert("No worker selected to rate.");
            return;
        }

        setSubmitting(true);
        try {
            if (isBulk) {
                const payload = finalTargets.map(t => ({
                    ratedProfileId: t.workerId,
                    hireId: t.hireId || null,
                    type: t.type || 'Direct_Hire', // Bulk ratings usually come from Job Management (Direct_Hire)
                    rating: rating,
                    review_text: review
                }));
                await apiClient.post('/ratings/bulk', { ratings: payload });
            } else {
                const target = finalTargets[0];
                if (!target.workerId) {
                    throw new Error("Worker ID is missing.");
                }
                await apiClient.post('/ratings', {
                    ratedProfileId: target.workerId,
                    hireId: target.hireId || null,
                    type: target.type || 'Direct_Hire',
                    rating: rating,
                    review_text: review
                });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error("[Rating Error]", err);
            alert(err.response?.data?.message || err.message || "Failed to submit rating.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white w-full max-w-md rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden animate-fade-in-up">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-yellow-50 text-yellow-500 rounded-3xl flex items-center justify-center mx-auto mb-4 text-3xl font-bold">
                        <FaStar />
                    </div>
                    <h3 className="text-2xl font-black text-slate-800">{isBulk ? 'Rate All Workers' : 'Rate Professional'}</h3>
                    <p className="text-slate-500 font-medium">
                        {isBulk ? `Applying feedback to ${finalTargets.length} professionals` : `How was your experience with ${finalTargets[0]?.workerName}?`}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="flex justify-center gap-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                onClick={() => setRating(star)}
                                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${rating >= star ? 'bg-yellow-400 text-white shadow-lg shadow-yellow-100' : 'bg-slate-100 text-slate-300 hover:bg-slate-200'}`}
                            >
                                <FaStar size={20} />
                            </button>
                        ))}
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Your Review (Optional)</label>
                        <textarea 
                            className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 ring-emerald-500/20 outline-none min-h-[100px] text-sm font-medium"
                            placeholder={isBulk ? "Provide same review for all..." : "Tell others about the quality of work..."}
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button type="button" onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black active:scale-95 transition-all">Cancel</button>
                        <button type="submit" disabled={submitting} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black shadow-lg active:scale-95 transition-all disabled:opacity-50">
                            {submitting ? 'Submitting...' : 'Submit Rating'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RatingModal;
